import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, MapPin, Users, Check, X, Clock } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};
const fmtCountdown = (iso) => {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'Expired';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m left`;
};

/**
 * Pro Dashboard card — every pending offer the logged-in pro must respond to.
 *
 * One Accept / Decline button per row. Decline opens a small reason prompt;
 * declining will also block the pro's calendar on the event date (a busy
 * record is written server-side so they can't double-book).
 */
const ProPendingOffersCard = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/pro/pending-offers`, { withCredentials: true });
      setOffers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load pending offers');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleAccept = async (o) => {
    if (busyId) return;
    setBusyId(o.slot_id);
    try {
      await axios.post(`${BACKEND_URL}/api/pro/offers/${o.booking_id}/${o.slot_id}/accept`, {}, { withCredentials: true });
      toast.success('Offer accepted — booking confirmed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Accept failed');
    }
    setBusyId(null);
  };
  const handleDecline = async (o) => {
    if (busyId) return;
    const reason = window.prompt('Reason for declining? (optional)\n\nNote: your calendar will be marked busy on this date.');
    if (reason === null) return; // user hit Cancel
    setBusyId(o.slot_id);
    try {
      await axios.post(`${BACKEND_URL}/api/pro/offers/${o.booking_id}/${o.slot_id}/decline`,
        { reason }, { withCredentials: true });
      toast.success('Offer declined · calendar blocked for that date');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Decline failed');
    }
    setBusyId(null);
  };

  if (loading) {
    return (
      <Card data-testid="pro-pending-loading">
        <CardContent className="py-6 text-sm text-text-secondary text-center">Checking pending offers…</CardContent>
      </Card>
    );
  }
  if (offers.length === 0) {
    return (
      <Card className="bg-white border-[#E8E1D5]" data-testid="pro-pending-empty">
        <CardContent className="py-6 text-sm text-text-secondary text-center">
          No pending offers. Customers' booking requests will appear here.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="pro-pending-offers-card">
      <CardHeader className="border-b border-[#E8E1D5]">
        <CardTitle className="font-heading text-2xl">
          Pending Booking Offers ({offers.length})
        </CardTitle>
        <CardDescription>
          Customers paid the advance — accept to lock the date, or decline (your calendar will be marked busy either way).
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-5 space-y-3">
        {offers.map((o) => (
          <div key={o.slot_id} className="border-2 border-[#E8E1D5] rounded p-3 bg-sand-50/40"
            data-testid={`pro-offer-${o.slot_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="font-bold text-sm">{o.name}</div>
                <div className="text-xs text-text-secondary">Booking · {o.display_id || o.booking_id.slice(-6)}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-heading text-xl font-bold text-brand-primary">₹{Number(o.amount).toLocaleString('en-IN')}</div>
                <div className="text-[10px] text-text-secondary">advance payable</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-text-secondary mb-3">
              {o.event_date && <span className="flex items-center gap-1"><Calendar size={12} />{o.event_date}</span>}
              {o.city && <span className="flex items-center gap-1"><MapPin size={12} />{o.city}</span>}
              {o.guests > 0 && <span className="flex items-center gap-1"><Users size={12} />{o.guests} guests</span>}
              {o.expires_at && <span className="flex items-center gap-1 text-amber-700 font-bold"><Clock size={12} />{fmtCountdown(o.expires_at)}</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => handleAccept(o)} disabled={busyId === o.slot_id}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-none"
                data-testid={`pro-offer-accept-${o.slot_id}`}>
                <Check size={14} className="mr-1" /> Accept
              </Button>
              <Button onClick={() => handleDecline(o)} disabled={busyId === o.slot_id}
                variant="outline" className="flex-1 border-red-300 text-red-700 hover:bg-red-50 rounded-none"
                data-testid={`pro-offer-decline-${o.slot_id}`}>
                <X size={14} className="mr-1" /> Decline
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default ProPendingOffersCard;
